import { Link, useLocation, useParams } from 'react-router-dom';
import { useGetUser } from '@/services/auth.service';
import { useProjects } from '@/services/project.service';
import { useSamples } from '@/services/sample.service';
import { useTranslation } from 'react-i18next';
import {
  FolderIcon,
  SettingsIcon,
  ServerIcon,
  LogInIcon,
  SproutIcon,
} from 'lucide-react';
import logo from '@/assets/Humeos_logotype_RGB_positive.png';
import { BASE } from '../basePath';

export default function Sidebar() {
  const { projectId, sampleId } = useParams();
  const { data: user } = useGetUser();
  const { data: projects } = useProjects();
  const { data: samples } = useSamples(projectId);
  const { t } = useTranslation();
  const location = useLocation();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const sortedProjects = [...(projects ?? [])].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  return (
    <aside className="w-64 flex-shrink-0 border-r border-[#e3e5df] bg-[#fbfbf9] flex flex-col">
      <div className="h-16 px-5 flex items-center border-b border-[#e3e5df]">
        <Link to={`${BASE}/project`} className="flex items-center gap-2.5">
          <img src={logo} alt="Humeos" className="h-8 w-8" />
          <span className="text-[17px] font-semibold tracking-tight text-[#1c2620]">Humeos</span>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {user?.isSuperAdmin && (
          <>
            <SectionHeader label={t('Admin')} />
            <NavLink
              to={`${BASE}/backOffice/monitoring`}
              icon={<ServerIcon className="h-4 w-4" />}
              label={t('Backoffice')}
              active={isActive(`${BASE}/backOffice`)}
            />
          </>
        )}

        <NavLink
          to={`${BASE}/project`}
          icon={<FolderIcon className="h-4 w-4" />}
          label={t('All projects')}
          active={location.pathname === `${BASE}/project`}
        />

        {sortedProjects.length > 0 && <SectionHeader label={t('Projects')} />}
        <div>
          {sortedProjects.map((p) => {
            const isCurrent = p.id === projectId;
            return (
              <div key={p.id}>
                <Link
                  to={`${BASE}/project/${p.id}`}
                  title={p.name}
                  className={`block px-3 py-1.5 rounded-md text-sm truncate transition ${
                    isCurrent
                      ? 'bg-[#e8efe9] text-[#1c2620] font-medium shadow-[inset_3px_0_0_#2d6a4f]'
                      : 'text-[#3a4339] hover:bg-[#eef0eb]'
                  }`}
                >
                  {p.name}
                </Link>
                {isCurrent && samples && samples.length > 0 && (
                  <div className="ml-3 mt-0.5 mb-1 border-l border-[#dadbd5] pl-2 space-y-0.5">
                    {samples.map((s) => {
                      const isCurrentSample = s.id === sampleId;
                      return (
                        <Link
                          key={s.id}
                          to={`${BASE}/project/${p.id}/${s.id}`}
                          title={s.name}
                          className={`flex items-center gap-2 px-2 py-1 rounded-md text-[13px] transition ${
                            isCurrentSample
                              ? 'bg-[#e8efe9] text-[#1c2620] font-medium'
                              : 'text-[#3a4339] hover:bg-[#eef0eb]'
                          }`}
                        >
                          <SproutIcon
                            className={`h-3 w-3 flex-shrink-0 ${
                              isCurrentSample
                                ? 'text-[#2d6a4f]'
                                : 'text-[#7a8077]'
                            }`}
                          />
                          <span className="truncate">{s.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      <div className="border-t border-[#e3e5df] p-3">
        {user ? (
          <Link
            to={`${BASE}/user`}
            className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-[#eef0eb] transition"
          >
            <div className="h-8 w-8 rounded-full bg-[#2d6a4f] text-white flex items-center justify-center text-xs font-semibold">
              {user.firstName?.[0]}
              {user.lastName?.[0]}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-[#1c2620] truncate">
                {user.firstName} {user.lastName}
              </span>
            </div>
            <SettingsIcon className="h-3.5 w-3.5 text-[#7a8077] ml-auto flex-shrink-0" />
          </Link>
        ) : (
          <Link
            to={`${BASE}/login`}
            className="flex items-center gap-2 justify-center w-full px-3 py-2 rounded-md text-sm font-medium bg-[#2d6a4f] text-white hover:bg-[#245741] transition"
          >
            <LogInIcon className="h-4 w-4" />
            {t('Login')}
          </Link>
        )}
      </div>
    </aside>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="pt-5 pb-1.5 px-3 text-[11px] font-medium uppercase tracking-[0.08em] text-[#8a9087]">
      {label}
    </div>
  );
}

function NavLink({
  to,
  icon,
  label,
  active,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
        active
          ? 'bg-[#e8efe9] text-[#1c2620] font-medium shadow-[inset_3px_0_0_#2d6a4f]'
          : 'text-[#3a4339] hover:bg-[#eef0eb]'
      }`}
    >
      <span className={active ? 'text-[#2d6a4f]' : 'text-[#7a8077]'}>
        {icon}
      </span>
      {label}
    </Link>
  );
}
